/**
 * Shop-the-look drawer — Seventh page-content fetch + looks carousel.
 * Open via `ShopTheLookOpener.open()` (HTW teaser, etc.).
 *
 * Add to cart → quick shop (stacked) → add with `_htw_id` / `_htw_title` line properties.
 */
class ShopTheLook extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.pageContent = this.querySelector("page-content");
    this.drawer = this.closest("sidebar-drawer");
    this.titleEl = this.drawer?.querySelector("[data-page-content-title]");
    this.defaultTitle = this.titleEl?.textContent?.trim() || "";
    this.titlePrefix = this.drawer?.dataset.titlePrefix?.trim() || "";
    this._htwSessionId = "";
    this._htwTitle = "";
    this._htwPickPending = false;
    this._htwPickButton = null;

    this.pageContent?.addEventListener("page-content:ready", event => this.#onContentReady(event));
    this.querySelector("[data-look-open-cart]")?.addEventListener("click", event => this.#openCartFromToast(event));
    this.addEventListener("click", event => this.#onPickSizeClick(event));

    if (!this._lookDrawerLayoutBound && this.drawer) {
      this._lookDrawerLayoutBound = true;
      this.drawer.addEventListener(
        "transitionend",
        event => {
          if (event.target !== this.drawer.querySelector(".kt-sidebar__content") || event.propertyName !== "transform") {
            return;
          }
          if (!this.drawer.classList.contains("kt-sidebar--opened")) return;
          const slider = this.querySelector("[data-look-carousel-slider]");
          if (!slider?.sliderEnabled) return;
          slider.resetSlider(true, false);
          slider.changeSlide(slider.index, "auto");
        },
        { passive: true }
      );
    }

    const quickShopDrawer = document.getElementById("quick-shop-drawer");
    if (!ShopTheLook._quickShopCloseBound && quickShopDrawer) {
      ShopTheLook._quickShopCloseBound = true;
      quickShopDrawer.addEventListener(
        "transitionend",
        event => {
          if (event.target !== quickShopDrawer.querySelector(".kt-sidebar__content") || event.propertyName !== "transform") {
            return;
          }
          if (quickShopDrawer.classList.contains("kt-sidebar--opened")) return;
          ShopTheLook.activeInstance?._cancelHtwPickPending();
          ShopTheLook.#clearQuickShopStacking();
        },
        { passive: true }
      );
    }

    if (quickShopDrawer) {
      this._onQuickShopSubmit = event => {
        if (!this._htwPickPending) return;
        const form = event.target.closest("form");
        if (form && quickShopDrawer.contains(form)) {
          this.#injectAttribution(form);
        }
      };
      quickShopDrawer.addEventListener("submit", this._onQuickShopSubmit, true);
    }

    ShopTheLook.activeInstance = this;

    window.ShopTheLookOpener = {
      open: (pageUrl, sectionId) => this.open(pageUrl, sectionId),
      hide: () => this.drawer?.hide(),
      showAddedToast: () => this.#showAddedToast(),
      handleQuickShopAdd: () => this.#handleQuickShopAdd()
    };
    window.dispatchEvent(new Event("shopTheLookDialogReady"));
  }

  disconnectedCallback() {
    const quickShopDrawer = document.getElementById("quick-shop-drawer");
    if (quickShopDrawer && this._onQuickShopSubmit) {
      quickShopDrawer.removeEventListener("submit", this._onQuickShopSubmit, true);
    }
    if (ShopTheLook.activeInstance === this) {
      ShopTheLook.activeInstance = null;
    }
  }

  open(pageUrl, sectionId = "helper-look-drawer") {
    let path = pageUrl != null && pageUrl !== "" ? String(pageUrl).trim() : "";
    if (path && !path.startsWith("/") && path.includes("/")) path = `/${path}`;
    if (!path) path = window.location.pathname;

    this._htwSessionId = ShopTheLook.createAttributionId();
    this._htwTitle = "";
    this._htwPickPending = false;
    this._htwPickButton = null;
    if (this.titleEl) this.titleEl.textContent = this.defaultTitle;
    const toast = this.querySelector("[data-look-toast]");
    if (toast) toast.hidden = true;

    if (sectionId && this.pageContent) {
      this.pageContent.setAttribute("section", sectionId);
    }

    const searchDrawer = document.getElementById("site-search-sidebar");
    if (searchDrawer?.classList.contains("kt-sidebar--opened")) {
      searchDrawer.hide();
    }

    const quickShopDrawer = document.getElementById("quick-shop-drawer");
    if (quickShopDrawer?.classList.contains("kt-sidebar--opened")) {
      quickShopDrawer.hide();
    }

    this.pageContent?.open(path);
  }

  static createAttributionId() {
    return `kit_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }

  static #clearQuickShopStacking() {
    document.getElementById("quick-shop-drawer")?.closest(".mount-drawer")?.classList.remove("mount-drawer--stacked-top");
  }

  #onPickSizeClick(event) {
    const btn = event.target.closest("[data-look-pick-size]");
    if (!btn || btn.disabled) return;

    const row = btn.closest("[data-look-outfit-row]");
    const url = row?.dataset.productUrl;
    if (!url) return;

    event.preventDefault();
    event.stopPropagation();

    if (!this._htwSessionId) {
      this._htwSessionId = ShopTheLook.createAttributionId();
    }

    this._htwPickPending = true;
    this._htwPickButton = btn;
    document.querySelector("#quick-shop-drawer quick-shop-content")?.open(url, btn);
    requestAnimationFrame(() => this.#syncQuickShopStacking());
  }

  #injectAttribution(form) {
    if (!form || !this._htwSessionId) return;

    form.querySelectorAll("[data-look-htw-property]").forEach(input => input.remove());

    const title = this._htwTitle?.trim() || this.#readDrawerLookTitle() || "How to wear it";
    const props = {
      _htw_id: this._htwSessionId,
      _htw_title: title
    };

    for (const [name, value] of Object.entries(props)) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = `properties[${name}]`;
      input.value = value;
      input.dataset.lookHtwProperty = "";
      form.appendChild(input);
    }
  }

  #readDrawerLookTitle() {
    const lookEl = this.titleEl?.querySelector(".kt-text-size--xlarge");
    if (lookEl?.textContent?.trim()) return lookEl.textContent.trim();
    return this.titleEl?.textContent?.trim() || "";
  }

  #handleQuickShopAdd() {
    if (!this._htwPickPending) return false;

    const button = this._htwPickButton;
    const variantTitle = this.#readQuickShopVariantTitle();
    this._htwPickPending = false;
    this._htwPickButton = null;
    this.#showProductAdded(button, variantTitle);
    this.#showAddedToast();
    document.getElementById("quick-shop-drawer")?.hide();
    ShopTheLook.#clearQuickShopStacking();
    return true;
  }

  _cancelHtwPickPending() {
    this._htwPickPending = false;
    this._htwPickButton = null;
  }

  #readQuickShopVariantTitle() {
    const data = document.querySelector("#quick-shop-drawer [data-js-variant-data]");
    if (!data?.textContent) return "";

    try {
      const variant = JSON.parse(data.textContent);
      const title = variant?.public_title || variant?.title || "";
      return title === "Default Title" ? "" : title.trim();
    } catch {
      return "";
    }
  }

  #showProductAdded(button, variantTitle) {
    if (!button?.isConnected) return;

    const row = button.closest("[data-look-outfit-row]");
    const addedStatus = row?.querySelector("[data-look-added-status]");
    const variantLabel = addedStatus?.querySelector("[data-look-added-variant]");
    if (!addedStatus || !variantLabel) return;

    variantLabel.textContent = variantTitle ? `— ${variantTitle}` : "";
    addedStatus.hidden = false;
  }

  #syncQuickShopStacking() {
    if (!this.drawer?.classList.contains("kt-sidebar--opened")) {
      ShopTheLook.#clearQuickShopStacking();
      return;
    }

    document.getElementById("quick-shop-drawer")?.closest(".mount-drawer")?.classList.add("mount-drawer--stacked-top");
  }

  #onContentReady(event) {
    const contentEl = event.detail?.contentEl;
    if (!contentEl) return;
    this.#initLooksCarousel(contentEl);
    this.#prefetchQuickShops(contentEl);
  }

  #prefetchQuickShops(scope) {
    const urls = [
      ...new Set(
        [...scope.querySelectorAll("[data-look-outfit-row][data-product-url]")]
          .map(row => row.dataset.productUrl?.trim())
          .filter(Boolean)
      )
    ];
    if (!urls.length) return;

    customElements.whenDefined("quick-shop-content").then(() => {
      const quickShop = document.querySelector("#quick-shop-drawer quick-shop-content");
      if (!quickShop?.prefetch) return;

      const prefetchNext = async () => {
        while (urls.length) {
          const url = urls.shift();
          await quickShop.prefetch(url, { view: "quick-view" });
        }
      };

      const concurrency = Math.min(3, urls.length);
      Promise.allSettled(Array.from({ length: concurrency }, () => prefetchNext()));
    });
  }

  #showAddedToast() {
    const toast = this.querySelector("[data-look-toast]");
    if (!toast) return;
    toast.hidden = false;
    clearTimeout(this._addedToastTimer);
    this._addedToastTimer = setTimeout(() => {
      toast.hidden = true;
    }, 6000);
  }

  #openCartFromToast(event) {
    event.preventDefault();
    this.querySelector("[data-look-toast]").hidden = true;
    this.drawer?.hide();
    requestAnimationFrame(() => document.getElementById("site-cart-sidebar")?.show());
  }

  static normalizePath(path) {
    if (path == null || path === "") return "";
    let value = String(path).trim();
    if (!value.startsWith("/")) value = `/${value}`;
    const q = value.indexOf("?");
    if (q >= 0) value = value.slice(0, q);
    return value.replace(/\/+$/, "") || value;
  }

  #setLookTitle(name) {
    const value = name?.trim();
    if (!value) return;

    this._htwTitle = value;

    if (!this.titleEl) return;

    if (this.titlePrefix) {
      this.titleEl.innerHTML = `<span class="kt-flex kt-flex--column kt-gap--xxxsmall"><span class="kt-text-size--xsmall kt-text-weight--bold kt-text-color--opacity">${this.titlePrefix}</span><span class="kt-text-size--xlarge">${value}</span></span>`;
      return;
    }

    this.titleEl.textContent = value;
  }

  #initLooksCarousel(scope) {
    const root = scope?.querySelector("[data-look-carousel]");
    if (!root) return;

    const slider = root.querySelector("[data-look-carousel-slider]");
    const outfitEl = root.querySelector("[data-look-carousel-outfit]");
    const errorEl = root.querySelector("[data-look-carousel-error]");
    if (!slider || !outfitEl) return;

    let shownPath = outfitEl.dataset.lookCurrentPath || "";

    const pathMatches = fetchPath =>
      shownPath && fetchPath && ShopTheLook.normalizePath(shownPath) === ShopTheLook.normalizePath(fetchPath);

    const findTemplate = fetchPath => {
      const want = ShopTheLook.normalizePath(fetchPath);
      if (!want) return null;

      for (const tpl of root.querySelectorAll("template[data-look-path]")) {
        const got = ShopTheLook.normalizePath(tpl.dataset.lookPath);
        if (got === want || got.endsWith(want) || want.endsWith(got)) return tpl;
      }
      return null;
    };

    const renderOutfit = slide => {
      const fetchPath = slide.dataset.lookPath?.trim();
      if (!fetchPath) return;

      if (pathMatches(fetchPath) && outfitEl.innerHTML.trim().length > 20) {
        errorEl?.setAttribute("hidden", "");
        this.#setLookTitle(slide.dataset.lookTitle);
        return;
      }

      const tpl = findTemplate(fetchPath);
      const outfit = tpl?.content.cloneNode(true).querySelector(".look-outfit");
      if (!outfit) {
        errorEl?.removeAttribute("hidden");
        return;
      }

      errorEl?.setAttribute("hidden", "");
      outfit.querySelector("[data-look-outfit-preview]")?.remove();
      outfitEl.replaceChildren(outfit);
      outfitEl.dataset.lookCurrentPath = fetchPath;
      shownPath = fetchPath;
      this.#prefetchQuickShops(outfitEl);
      this._htwSessionId = ShopTheLook.createAttributionId();
      this.#setLookTitle(slide.dataset.lookTitle);
    };

    const getCurrentSlide = () => {
      const items = slider.items;
      if (!items?.length) return null;
      return slider.indexedItems?.[slider.index] || items[slider.index] || items[0];
    };

    const refreshSlider = () => {
      if (typeof slider.resetSlider === "function") {
        slider.resetSlider(false, false);
      }
    };

    const onSlideChange = () => {
      const slide = getCurrentSlide();
      if (slide) renderOutfit(slide);
    };

    const initSlider = () => {
      if (!slider.items?.length) return;

      slider.addEventListener("change", onSlideChange);
      refreshSlider();
      onSlideChange();
    };

    if (slider.sliderEnabled) {
      initSlider();
    } else {
      slider.addEventListener("ready", initSlider, { once: true });
    }
  }
}

if (!customElements.get("shop-the-look")) {
  customElements.define("shop-the-look", ShopTheLook);
}
